import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import ChevronRight from 'common/Icons/ChevronRight';
import GradientBorderButton from 'components/lib/GradientBorderButton';
import { canAccessAICompetitiveIntelligence, canAccessAISpeedToLead } from 'helpers/permissions';
import { renderBoldMarkup } from 'helpers/renderBoldMarkup';
import { useAppSelector } from 'store/hook';
import { CmiDigestWatchlistCommunityDetail } from 'types/cmiDigestWatchlist';

import CommunitiesToWatchPromoBanner from './CommunitiesToWatchPromoBanner';

const DetailsCard = styled.div`
  border: 1px solid var(--line-gray);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

const LeftContent = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
  min-width: 0;
`;

const BackIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 4px;
  border: 1px solid var(--line-gray);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const Content = styled.div`
  min-width: 0;
  width: 100%;
`;

const Title = styled.div`
  font-size: 24px;
  font-family: var(--font-bold);
`;

const TagsRow = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  font-size: 12px;
  padding: 6px 10px;
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  background: var(--white);
  color: var(--text-secondary);
`;

const InsightHeader = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-family: var(--font-bold);
`;

const InsightIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const InsightDivider = styled.div`
  height: 1px;
  margin: 10px 0;
  background-image: linear-gradient(to left, var(--flamingo-pink), var(--competitor-purple));
`;

const AiSummaryList = styled.ul`
  margin: 0;
  padding-left: 0;
  list-style-type: '· ';
  list-style-position: inside;
`;

const AiSummaryLine = styled.li`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-bottom: 6px;

  strong {
    color: var(--text-primary);
    font-family: var(--font-bold);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const PromoBannerRow = styled.div`
  margin-top: 20px;
`;

const RightPanel = styled.div`
  position: relative;
  padding-left: 20px;
  min-width: 220px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 1px;
    background: linear-gradient(to bottom, #fafafa 0%, var(--line-gray) 50%, #fafafa 100%);
  }
`;

const PanelTitle = styled.div`
  font-size: 10px;
  letter-spacing: 2px;
  color: var(--text-secondary);
  margin-bottom: 10px;
`;

const MetricsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const MetricLabel = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const MetricValue = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: var(--text-secondary);
`;

const RankingButtonRow = styled.div`
  margin-top: 20px;
`;

type Props = {
  data: CmiDigestWatchlistCommunityDetail;
  runId: number;
};

const CommunitiesToWatchDetailsCard = ({ data, runId }: Props) => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const canViewRankingDetails = canAccessAICompetitiveIntelligence(currentUser) || canAccessAISpeedToLead(currentUser);

  return (
    <DetailsCard>
      <LeftContent>
        <BackIcon onClick={() => navigate(`/communities-to-watch?runId=${runId}`)}>
          <img src="/icons/chevron-left-black.svg" alt="back" width="24px" height="24px" />
        </BackIcon>

        <Content>
          <Title>{data.community_name}</Title>
          <TagsRow>
            {data.needs_attention && <Tag>Needs Attention</Tag>}
            {(data.notable_items ?? []).map((item) => (
              <Tag key={item}>{item}</Tag>
            ))}
          </TagsRow>

          <InsightHeader>
            <InsightIcon src="/icons/ai-blob.svg" alt="" />
            AI Summary
          </InsightHeader>
          <InsightDivider />
          <AiSummaryList>
            {(data.summary ?? []).map((line, index) => (
              <AiSummaryLine key={index}>{renderBoldMarkup(line)}</AiSummaryLine>
            ))}
          </AiSummaryList>

          <PromoBannerRow>
            <CommunitiesToWatchPromoBanner runId={runId} />
          </PromoBannerRow>
        </Content>
      </LeftContent>

      {(data.supporting_context ?? []).length > 0 && (
        <RightPanel>
          <PanelTitle>NOTABLE ITEMS</PanelTitle>
          <MetricsList>
            {data.supporting_context.map((metric) => (
              <div key={metric.label}>
                <MetricLabel>{metric.label}</MetricLabel>
                <MetricValue>{metric.value}</MetricValue>
              </div>
            ))}
          </MetricsList>
          {canViewRankingDetails && (
            <RankingButtonRow>
              <GradientBorderButton
                size="small"
                onClick={() => navigate(`/ai-competitive-intelligence?communityId=${data.community_id}`)}
              >
                Ranking Details <ChevronRight fill="var(--competitor-purple)" width={16} height={16} />
              </GradientBorderButton>
            </RankingButtonRow>
          )}
        </RightPanel>
      )}
    </DetailsCard>
  );
};

export default CommunitiesToWatchDetailsCard;
